import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Badge } from '@/components/ui'
import { getComparisonPageCounts, getComparisons } from '@/data/comparisons'

export function CompareHubPage() {
  const comparisons = getComparisons()
  const counts = getComparisonPageCounts()

  return (
    <div className="min-h-screen bg-white">
      <section className="py-section-lg px-6 bg-white border-b border-line-gray">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-headline text-4xl sm:text-5xl font-light text-navy-900 tracking-tight">
            Brand Comparisons
          </h1>
          <p className="mt-6 text-lg text-muted-navy leading-relaxed">
            Explore <span className="font-medium">{counts.comparisons}</span> head-to-head matchups
            built from client-supplied comparison copy.
          </p>
          </div>
        </div>
      </section>

      <section className="py-section-lg px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {comparisons.map((c) => {
              const b1 = `/brand-images/${c.brand1.slug}.webp`
              const b2 = `/brand-images/${c.brand2.slug}.webp`

              return (
                <Link
                  key={c.slug}
                  to={`/brands/compare/${c.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
                >
                  {/* Image Area (match BrandsPage feel) */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 grid grid-cols-2 opacity-90">
                      <div
                        className="bg-cover bg-center bg-no-repeat bg-gray-200"
                        style={{ backgroundImage: `url('${b1}')` }}
                      />
                      <div
                        className="bg-cover bg-center bg-no-repeat bg-gray-200"
                        style={{ backgroundImage: `url('${b2}')` }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />

                    {/* Decorative center mark (like BrandCardImage initial) */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-6xl font-headline font-light text-navy-900/10 group-hover:scale-110 transition-transform duration-500">
                        VS
                      </span>
                    </div>

                    {/* Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge
                        variant="secondary"
                        className="bg-white/90 text-navy-900 backdrop-blur-sm"
                      >
                        Compare
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors">
                      {c.brand1.name} vs {c.brand2.name}
                    </h3>
                    <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase">
                      Head-to-head
                    </p>
                    <p className="mt-4 text-muted-navy text-sm leading-relaxed line-clamp-3">
                      {c.seo.description}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {[...new Set(c.modelMatchups.map((m) => m.category))].slice(0, 3).map((cat) => (
                        <span
                          key={cat}
                          className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy"
                        >
                          {cat}
                        </span>
                      ))}
                      {new Set(c.modelMatchups.map((m) => m.category)).size > 3 && (
                        <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy">
                          +more
                        </span>
                      )}
                    </div>

                    <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                      <span>Explore</span>
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

